import type {
    ReportEventStatus,
    ReportFile,
    ReportRequest,
    ReportTemplate,
    ReportTemplateDetail,
    ReportType,
    ReportUsageEvent,
    ReportUsagePoint,
    ReportUsageSummary,
} from "@/entities/report/model/types";

function isLocalOrStagingHost(): boolean {
    if (typeof window === "undefined") return process.env.NODE_ENV !== "production";
    const { hostname } = window.location;
    return hostname === "localhost" || hostname === "127.0.0.1" || hostname.includes("staging");
}

/** Local e staging, para trabalhar nos relatórios sem o report-service; a flag nunca vale em produção. */
export function isReportMockEnabled(): boolean {
    return process.env.NEXT_PUBLIC_PLATFORM_MOCK === "true" && isLocalOrStagingHost();
}

const DAY_MS = 86_400_000;
const MOCK_TYPES: ReportType[] = ["pdf", "pdf", "pdf", "xlsx", "csv", "docx"];
const MOCK_TEMPLATES = ["wallet", "project_diary", "purchase_order", "timesheet"];

const templates = new Map<string, ReportTemplateDetail>(
    [
        { id: "tpl-wallet", name: "wallet", html: "<h1>{{ title }}</h1>\n<p>{{ owner }}</p>" },
        {
            id: "tpl-diary",
            name: "project_diary",
            html: "<h1>{{ project }}</h1>\n<ul>{% for entry in entries %}<li>{{ entry }}</li>{% endfor %}</ul>",
        },
    ].map((template, index): [string, ReportTemplateDetail] => {
        const at = new Date(Date.now() - (index + 3) * DAY_MS).toISOString();
        return [template.id, { ...template, createdAt: at, updatedAt: at }];
    }),
);

const events: ReportUsageEvent[] = Array.from({ length: 120 }, (_, index) => {
    const failed = index % 11 === 0;
    return {
        id: `evt-${index}`,
        createdAt: new Date(Date.now() - index * (DAY_MS / 4)).toISOString(),
        reportType: MOCK_TYPES[index % MOCK_TYPES.length],
        template: MOCK_TEMPLATES[index % MOCK_TEMPLATES.length],
        status: failed ? "failed" : "success",
        sizeBytes: failed ? 0 : 18_000 + ((index * 7919) % 240_000),
    };
});

const withinDays = (days: number) => {
    const since = Date.now() - days * DAY_MS;
    return events.filter((event) => new Date(event.createdAt).getTime() >= since);
};

const toSummary = ({ id, name, createdAt, updatedAt }: ReportTemplateDetail): ReportTemplate => ({
    id,
    name,
    createdAt,
    updatedAt,
});

function requireTemplate(id: string): ReportTemplateDetail {
    const template = templates.get(id);
    if (!template) throw new Error(`template ${id} não existe no mock`);
    return template;
}

export async function getMockUsageSummary(days: number): Promise<ReportUsageSummary> {
    const scoped = withinDays(days);
    const success = scoped.filter((event) => event.status === "success").length;
    const byType: Partial<Record<ReportType, number>> = {};
    scoped.forEach((event) => {
        byType[event.reportType] = (byType[event.reportType] ?? 0) + 1;
    });
    return {
        total: scoped.length,
        success,
        failed: scoped.length - success,
        success_rate: scoped.length ? success / scoped.length : 0,
        by_type: byType,
    };
}

export async function getMockUsageTimeseries(days: number): Promise<ReportUsagePoint[]> {
    const counts = new Map<string, number>();
    withinDays(days).forEach((event) => {
        const date = event.createdAt.slice(0, 10);
        counts.set(date, (counts.get(date) ?? 0) + 1);
    });
    return [...counts.entries()]
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => a.date.localeCompare(b.date));
}

export async function listMockUsageEvents(limit: number, status?: ReportEventStatus): Promise<ReportUsageEvent[]> {
    return events.filter((event) => !status || event.status === status).slice(0, limit);
}

export async function listMockTemplates(): Promise<ReportTemplate[]> {
    return [...templates.values()].map(toSummary).sort((a, b) => a.name.localeCompare(b.name));
}

export async function getMockTemplate(id: string): Promise<ReportTemplateDetail> {
    return { ...requireTemplate(id) };
}

export async function createMockTemplate(name: string, html: string): Promise<ReportTemplate> {
    const at = new Date().toISOString();
    const template: ReportTemplateDetail = { id: `tpl-${Date.now()}`, name, html, createdAt: at, updatedAt: at };
    templates.set(template.id, template);
    return toSummary(template);
}

export async function updateMockTemplate(id: string, html: string): Promise<ReportTemplate> {
    const template = { ...requireTemplate(id), html, updatedAt: new Date().toISOString() };
    templates.set(id, template);
    return toSummary(template);
}

export async function removeMockTemplate(id: string): Promise<void> {
    templates.delete(id);
}

/** Sem o renderizador, o mock devolve o HTML: a tela avisa que não há PDF para pré-visualizar. */
export async function previewMockTemplate(id: string): Promise<Blob> {
    return new Blob([requireTemplate(id).html], { type: "text/html" });
}

export async function runMockPlayground(request: ReportRequest): Promise<ReportFile> {
    const payload = request.type === "pdf" ? request.data : request.rows;
    return {
        blob: new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" }),
        filename: `${request.name}.json`,
    };
}
